import React, { useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import SingleColumnTemplate from '../../../../ui/layouts/SingleColumnTemplate.jsx';

const PROGRAMS_QUERY = gql`
  query SprintPrograms($status: String) {
    sprintPrograms(status: $status) {
      id title slug status level duration_days price_cents featured enrollment_count avg_rating
      taskTemplates { id }
      coachProfile { id name }
    }
  }
`;

const PUBLISH_MUTATION = gql`
  mutation PublishProgram($id: ID!) {
    publish_sprint_program(id: $id) {
      id
      status
    }
  }
`;

const UPDATE_STATUS_MUTATION = gql`
  mutation UpdateProgramStatus($id: ID!, $status: String!) {
    update_sprint_program(id: $id, status: $status) {
      id
      status
    }
  }
`;

export default function AdminMarketplaceContainer() {
  // status: null → return all statuses (server only filters when present)
  const { data, loading, error } = useQuery(PROGRAMS_QUERY, { variables: { status: null } });
  const programs = data?.sprintPrograms || [];
  const [tab, setTab] = useState('review');
  const [publishProgram] = useMutation(PUBLISH_MUTATION, { refetchQueries: [{ query: PROGRAMS_QUERY, variables: { status: null } }] });
  const [updateProgramStatus] = useMutation(UPDATE_STATUS_MUTATION, { refetchQueries: [{ query: PROGRAMS_QUERY, variables: { status: null } }] });
  const grouped = useMemo(() => {
    return {
      review: programs.filter((p) => p.status === 'review'),
      draft: programs.filter((p) => p.status === 'draft'),
      published: programs.filter((p) => p.status === 'published'),
    };
  }, [programs]);

  const coaches = useMemo(() => {
    const map = new Map();
    for (const p of programs) {
      const c = p.coachProfile;
      if (!c) continue;
      const key = c.id;
      if (!map.has(key)) {
        map.set(key, {
          id: c.id,
          name: c.name,
          programs: 0,
          enrollments: 0,
          rating: null, // optional if available elsewhere
        });
      }
      const entry = map.get(key);
      entry.programs += 1;
      entry.enrollments += (p.enrollment_count || 0);
    }
    return Array.from(map.values());
  }, [programs]);

  return (
    <SingleColumnTemplate title="Marketplace Administration" subtitle="Manage programs, coaches, and marketplace performance" variant="wide">
      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="text-sm text-muted-foreground mb-2">Programs</div>
          <div className="flex items-end justify-between">
            <div className="text-3xl font-bold">{programs.length}</div>
            <div className="text-xs text-muted-foreground">+3 this month</div>
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="text-sm text-muted-foreground mb-2">Active Enrollments</div>
          <div className="flex items-end justify-between">
            <div className="text-3xl font-bold">{programs.reduce((n, p) => n + (p.enrollment_count || 0), 0)}</div>
            <div className="text-xs text-muted-foreground">+12 this week</div>
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="text-sm text-muted-foreground mb-2">Completion Rate</div>
          <div className="flex items-end justify-between">
            <div className="text-3xl font-bold">40%</div>
            <div className="text-xs text-muted-foreground">+5% vs last month</div>
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="text-sm text-muted-foreground mb-2">Pending Reviews</div>
          <div className="flex items-end justify-between">
            <div className="text-3xl font-bold">{grouped.review.length}</div>
            <div className="text-xs text-muted-foreground">Needs attention</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-4">
        {['review','all','coaches','analytics'].map((t) => (
          <button
            key={t}
            onClick={() => {
              setTab(t);
              try {
                if (window.ahoy && typeof window.ahoy.track === 'function') {
                  window.ahoy.track('admin_marketplace_tab_click', { tab: t });
                }
              } catch (_) {}
            }}
            className={`px-3 py-1.5 rounded-lg border ${tab === t ? 'bg-card border-border shadow-sm' : 'border-transparent bg-muted'} focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)] hover:bg-[var(--succession-hover-bg)]`}
          >
            {t === 'review' ? 'Review Queue' : t === 'all' ? 'All Programs' : t === 'coaches' ? 'Coaches' : 'Analytics'}
          </button>
        ))}
      </div>

      {loading && <div className="rounded-2xl border border-border bg-card p-6">Loading...</div>}
      {error && <div className="rounded-2xl border border-border bg-card p-6">Error loading programs</div>}

      {!loading && !error && (
        <>
          {tab === 'review' && (
            <section className="rounded-2xl border border-border bg-card p-0 overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4">
                <h2 className="text-lg font-semibold">Review Queue</h2>
              </div>
              <div className="px-6 pb-2">
                <div className="grid grid-cols-12 text-xs text-muted-foreground">
                  <div className="col-span-4">Program</div>
                  <div className="col-span-2">Coach</div>
                  <div className="col-span-1">Level</div>
                  <div className="col-span-1">Duration</div>
                  <div className="col-span-1">Price</div>
                  <div className="col-span-1">Tasks</div>
                  <div className="col-span-2 text-right">Actions</div>
                </div>
              </div>
              {grouped.review.length === 0 ? (
                <div className="rounded-xl border border-border m-6 p-12 text-center text-muted-foreground">
                  No programs pending review
                </div>
              ) : (
                <div className="divide-y">
                  {grouped.review.map((p) => (
                    <div key={p.id} className="px-6 py-3 grid grid-cols-12 items-center">
                      <div className="col-span-4 min-w-0">
                        <p className="font-medium truncate">{p.title}</p>
                      </div>
                      <div className="col-span-2 truncate">{p.coachProfile?.name || '—'}</div>
                      <div className="col-span-1 capitalize">{p.level}</div>
                      <div className="col-span-1">{p.duration_days}d</div>
                      <div className="col-span-1">{p.price_cents ? `$${(p.price_cents/100).toLocaleString()}` : 'Free'}</div>
                      <div className="col-span-1">{p.taskTemplates?.length || 0}</div>
                      <div className="col-span-2 text-right flex items-center justify-end gap-2">
                        <a href={`/sprints/programs/${p.slug}`} className="rounded-lg border border-border bg-card px-3 py-1.5 text-sm hover:bg-[var(--succession-hover-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]">View</a>
                        <button
                          className="rounded-lg border border-border bg-card px-3 py-1.5 text-sm hover:bg-[var(--succession-hover-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                          onClick={() => publishProgram({ variables: { id: p.id } })}
                          title="Approve and publish"
                        >
                          Approve
                        </button>
                        <button
                          className="rounded-lg border border-border bg-card px-3 py-1.5 text-sm hover:bg-[var(--succession-hover-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                          onClick={() => updateProgramStatus({ variables: { id: p.id, status: 'draft' } })}
                          title="Decline back to draft"
                        >
                          Decline
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {tab === 'all' && (
            <section className="rounded-2xl border border-border bg-card p-0 overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4">
                <h2 className="text-lg font-semibold">All Programs</h2>
                <div className="flex items-center gap-2 text-xs">
                  <span className="rounded-full border border-border bg-card px-2 py-1">
                    {grouped.published.length} Published
                  </span>
                  <span className="rounded-full border border-border bg-card px-2 py-1">
                    {grouped.draft.length} Drafts
                  </span>
                </div>
              </div>

              {/* Table header */}
              <div className="px-6 pb-2">
                <div className="grid grid-cols-12 text-xs text-muted-foreground">
                  <button className="col-span-6 text-left font-medium hover:text-foreground focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]">Program</button>
                  <button className="col-span-2 text-left font-medium hover:text-foreground focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]">Status</button>
                  <button className="col-span-1 text-left font-medium hover:text-foreground focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]">Enrollments</button>
                  <button className="col-span-1 text-left font-medium hover:text-foreground focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]">Rating</button>
                  <button className="col-span-1 text-left font-medium hover:text-foreground focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]">Featured</button>
                  <div className="col-span-1 text-right font-medium">Actions</div>
                </div>
              </div>

              {/* Rows */}
              <div className="divide-y">
                {programs.map((p) => (
                  <div key={p.id} className="px-6 py-3 grid grid-cols-12 items-center">
                    <div className="col-span-6 min-w-0">
                      <p className="font-medium truncate">{p.title}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {p.duration_days} days • {p.level}
                      </p>
                    </div>
                    <div className="col-span-2">
                      <span className="inline-block rounded px-2 py-0.5 text-xs border border-border">
                        {p.status}
                      </span>
                    </div>
                    <div className="col-span-1 text-sm">{p.enrollment_count || 0}</div>
                    <div className="col-span-1 text-sm">
                      {p.avg_rating ? (
                        <span className="inline-flex items-center gap-1">
                          <span className="text-yellow-500">★</span>
                          {Number(p.avg_rating).toFixed(1)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </div>
                    <div className="col-span-1">
                      <span className={`text-yellow-500 ${p.featured ? '' : 'opacity-30'}`}>★</span>
                    </div>
                    <div className="col-span-1 text-right">
                      <a
                        href={`/sprints/programs/${p.slug}`}
                        className="rounded-lg border border-border bg-card px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                      >
                        View
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {tab === 'coaches' && (
            <section className="rounded-2xl border border-border bg-card p-6">
              <h2 className="text-lg font-semibold mb-4">Coaches</h2>
              {coaches.length === 0 ? (
                <div className="rounded-xl border border-border p-12 text-center text-muted-foreground">
                  No coaches found
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {coaches.map((c) => (
                    <div key={c.id} className="rounded-xl border border-border bg-card p-5">
                      <div className="flex items-center justify-between mb-3">
                        <div className="font-semibold truncate">{c.name}</div>
                        <span className="rounded-full border border-border bg-card px-2 py-0.5 text-xs">Verified</span>
                      </div>
                      <div className="grid grid-cols-3 text-center text-sm py-3 border-y border-border">
                        <div>
                          <div className="text-xl font-bold">{c.programs}</div>
                          <div className="text-muted-foreground">Programs</div>
                        </div>
                        <div>
                          <div className="text-xl font-bold">{c.enrollments}</div>
                          <div className="text-muted-foreground">Enrollments</div>
                        </div>
                        <div>
                          <div className="text-xl font-bold">{c.rating ? Number(c.rating).toFixed(1) : '—'}</div>
                          <div className="text-muted-foreground">Rating</div>
                        </div>
                      </div>
                      <div className="mt-3 text-right">
                        <a href={`/coaches/${c.id}`} className="rounded-lg border border-border bg-card px-3 py-1.5 text-sm hover:bg-[var(--succession-hover-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]">
                          View Profile
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {tab === 'analytics' && (
            <section className="rounded-2xl border border-border bg-card p-6">
              <h2 className="text-lg font-semibold mb-4">Marketplace Analytics</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="rounded-xl border border-border bg-card p-4">
                  <div className="font-medium mb-2">Top Programs by Enrollment</div>
                  <div className="space-y-3">
                    {programs.slice(0,3).map((p) => (
                      <div key={p.id}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="truncate">{p.title}</span>
                          <span className="text-muted-foreground">{p.enrollment_count || 0}</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div className="h-2 bg-primary rounded-full" style={{width: `${Math.min(100, (p.enrollment_count || 0) * 10)}%`}} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-xl border border-border bg-card p-4">
                  <div className="font-medium mb-2">Enrollment Status Distribution</div>
                  <div className="text-sm text-muted-foreground">Placeholder — hook up to real metrics later.</div>
                </div>
              </div>
            </section>
          )}
        </>
      )}
    </SingleColumnTemplate>
  );
}


